import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  Tooltip,
  useDisclosure,
  useColorModeValue,
  useToast,
  Heading,
} from "@chakra-ui/react";
import { FaClipboardList } from "react-icons/fa";
import { getSocket } from "../../Chat/socketClient";
import { getRecadosNaoVistosContagem } from "@/services/recadoService";
import { getTarefasPendentesContagem } from "@/services/tarefaService";
import TarefaRecadoSidebar from "./TarefaRecadoSidebar";

/**
 * Badge = tarefas pendentes (onde o usuário é responsável ou enviou) + recados não vistos
 * (onde o usuário é destinatário). Notificação/toast de "novo recado" só faz sentido para o destinatário;
 * o evento Socket `recado:novo` é emitido apenas para a sala do destinatário no backend.
 */
export default function TarefaRecadoDrawer({ clinicaId, children }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bg = useColorModeValue("white", "gray.800");
  const toast = useToast();
  const [totalBadge, setTotalBadge] = useState(0);
  const prevTotal = useRef(-1);
  const prevTarefas = useRef(-1);
  const prevRecados = useRef(-1);

  const refreshBandejaCounts = useCallback(async () => {
    if (!clinicaId) {
      setTotalBadge(0);
      return;
    }
    try {
      const [tRes, rRes] = await Promise.all([
        getTarefasPendentesContagem(clinicaId).catch(() => ({ pendentes: 0 })),
        getRecadosNaoVistosContagem(clinicaId).catch(() => ({ naoVistos: 0 })),
      ]);
      const nT = typeof tRes?.pendentes === "number" ? tRes.pendentes : 0;
      const nR = typeof rRes?.naoVistos === "number" ? rRes.naoVistos : 0;
      const total = nT + nR;

      if (prevTotal.current >= 0 && total > prevTotal.current) {
        const recSubiu = prevRecados.current >= 0 && nR > prevRecados.current;
        const tarSubiu = prevTarefas.current >= 0 && nT > prevTarefas.current;
        if (recSubiu && !tarSubiu) {
          toast({
            title: "Novo recado",
            description: "Você recebeu um recado. Abra Tarefas/Recados para ver.",
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        } else if (tarSubiu && !recSubiu) {
          toast({
            title: "Tarefas pendentes",
            description: "Há novas tarefas na sua bandeja.",
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Novidade na bandeja",
            description: "Há tarefas ou recados pendentes.",
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        }
      }
      prevTotal.current = total;
      prevTarefas.current = nT;
      prevRecados.current = nR;
      setTotalBadge(total);
    } catch {
      /* ignore */
    }
  }, [clinicaId, toast]);

  useEffect(() => {
    prevTotal.current = -1;
    prevTarefas.current = -1;
    prevRecados.current = -1;
    refreshBandejaCounts();
    const id = setInterval(refreshBandejaCounts, 60_000);
    return () => clearInterval(id);
  }, [refreshBandejaCounts]);

  useEffect(() => {
    if (isOpen && clinicaId) {
      refreshBandejaCounts();
    }
  }, [isOpen, clinicaId, refreshBandejaCounts]);

  useEffect(() => {
    const s = getSocket();
    if (!s) return undefined;
    const onLembrete = () => {
      refreshBandejaCounts();
      toast({
        title: "Lembrete de tarefa",
        status: "info",
        duration: 4500,
        isClosable: true,
      });
    };
    const onRecadoNovo = (payload) => {
      if (String(payload?.clinicaId || "") !== String(clinicaId || "")) return;
      refreshBandejaCounts();
    };
    s.on("tarefa:lembrete", onLembrete);
    s.on("recado:novo", onRecadoNovo);
    return () => {
      s.off("tarefa:lembrete", onLembrete);
      s.off("recado:novo", onRecadoNovo);
    };
  }, [refreshBandejaCounts, toast, clinicaId]);

  return (
    <>
      {typeof children === 'function' ? children({ onOpen, totalBadge }) : children}

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent bg={bg}>
          <DrawerCloseButton />
          <DrawerHeader pr={10}><Heading fontSize="lg">Tarefas / Recados</Heading></DrawerHeader>
          <DrawerBody p={0}>
            <Box p={3}>
              <TarefaRecadoSidebar clinicaId={clinicaId} onBandejaCountsRefresh={refreshBandejaCounts} />
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
